import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Modal } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import './ReenviarAvisoAlta.css';

/* ═══════════════════════════════════════════════════════════════════════════
   REENVIAR EL AVISO DE ALTA — modal de destinatarios con casillas

   Spec: docs/specs/2026-09-10-reenvio-aviso-alta-autoexcluido.md

   ── Por qué existe ────────────────────────────────────────────────────────
   Entre el 09/05/2026 y el hotfix, el aviso de alta NO SALIÓ: `FormularioAuto`
   pedía la lista de destinatarios con `fetch` nativo, que no pasa por el
   interceptor de axios que agrega `X-Auth-Token`; el endpoint había pasado a
   pedir token, contestaba 401, `data.map` explotaba y el `catch` sólo hacía
   `console.error`. La pantalla decía "Los datos se han cargado correctamente".
   Quedaron ~4 meses de altas sin notificar a las salas de juego.

   De ahí salen las dos reglas duras de este archivo:

     · S2 — NINGÚN error se traga. Todo fallo termina como texto en pantalla,
       con el motivo. No hay un solo `console.error` que sea la única salida.
     · El correo lo arma y lo manda EL SERVIDOR. Acá se mandan el ID del
       autoexcluido y los emails tildados, nada más. El navegador no vuelve a
       armar texto legal ni a escribir en la colección `mail`.

   ── Por qué react-bootstrap y no `Dialogo` ────────────────────────────────
   Este archivo tiene que viajar IDÉNTICO a las tres ramas vivas (A4): en
   `main` la ficha se llama `AutoexcluidosDetalle.jsx` y en
   `feature/home-comercial` `FichaPersona.jsx`, pero el modal es el mismo
   componente. `Dialogo`, `pa-*` y `API_BASE` sólo existen en
   `feature/home-comercial`; `react-bootstrap` y `axios` están en las tres. Se
   eligió archivo idéntico sobre coherencia visual — la decisión y su costo
   están escritos en la spec. Por eso también la base de la API va acá adentro
   y los colores del `.css` van completos, sin tokens de `panel.css`.

   ── Trampa 4 del repo: esto SALE POR PORTAL ───────────────────────────────
   `Modal` de react-bootstrap se monta en el `body`, o sea FUERA de `.pa-page`
   y de cualquier contenedor que defina variables CSS. Todo color de
   `ReenviarAvisoAlta.css` está escrito literal por ese motivo. Si alguien
   agrega ahí un `var(--algo)`, se rompe en silencio.

   ── S7 — el gate de rol es presentacional ─────────────────────────────────
   Lo decide el servidor (`requirePermission("autoexcluidos:editar")`). El
   front no puede: `AuthContext` expone `role`, `realRole` y `esSuperadmin`,
   no la lista de permisos. Si el servidor contesta 403, se muestra el 403.
   ═══════════════════════════════════════════════════════════════════════════ */

/* La base va literal y no importada: `src/api/base.js` sólo existe en
   `feature/home-comercial` y este archivo tiene que compilar igual en las
   tres ramas (A4). El `process.env` deja que el `.env` de esa rama siga
   mandando donde existe. S7: esto va al bundle y es público — es
   configuración, no un secreto. */
const API =
  process.env.REACT_APP_API_BASE ||
  'https://us-central1-inprojuy-f1173.cloudfunctions.net/app';

const RUTA_DESTINATARIOS = '/aviso-alta/destinatarios';
const RUTA_REENVIO = '/reenviar-aviso-alta';

const norm = (e) => String(e || '').trim().toLowerCase();

/* ── Qué hacer con cada código ────────────────────────────────────────────
   El `message` del servidor dice QUÉ pasó; esto agrega QUÉ HACER, que el
   servidor no puede saber. Los dos casos que lo necesitan de verdad:

     · `error-interno` — verificado contra el backend ya implementado: el 500
       contesta siempre `message: "No se pudo reenviar el aviso"` y el detalle
       queda sólo en el log. Sin esta línea, la pantalla diría "no se pudo" y
       nada más, que es el modo de falla que originó toda la feature.
     · `sin-superadmins` — sólo aparece en la prueba en seco, y se lee como si
       faltaran destinatarios cuando en realidad falta a quién mandarle la
       PRUEBA. Son dos problemas distintos y se arreglan en lugares distintos. */
const QUE_HACER = {
  'error-interno': 'El servidor no dice más: el detalle quedó en su log. Probá de nuevo y, si se repite, pedí que lo revisen anotando la hora exacta.',
  'sin-superadmins': 'La prueba en seco se manda a los superadmin, y ninguno tiene correo cargado. El envío real no depende de esto.',
  'destinatarios-invalidos': 'La lista pudo cambiar mientras la ventana estaba abierta. Cerrá y volvé a abrir para traerla de nuevo.',
  'sin-destinatarios-configurados': 'Cargalos en Panel de Notificaciones, en el tipo Alta de autoexcluido.',
  'datos-incompletos': 'El aviso no sale con datos faltando, y no se rellenan con un guión: hay que completarlos en la ficha antes de reenviar.',
  'autoexcluido-inactivo': 'Sólo se reenvía el aviso de una autoexclusión vigente.',
};

/* ── Errores: del `catch` a la pantalla, siempre con motivo ────────────────
   Devuelve `{ codigo, mensaje }`. `codigo` es el campo nuevo que agregan los
   dos endpoints de esta feature; los 401/403 los tira el middleware viejo y
   NO tienen `codigo` (no se lo tocamos, A5), así que esos se discriminan por
   status. */
function leerError(error, porDefecto) {
  const res = error && error.response;
  const cuerpo = (res && res.data) || null;
  const codigo = (cuerpo && cuerpo.codigo) || null;
  const delServidor = cuerpo && (cuerpo.message || cuerpo.error);

  if (res && res.status === 401) {
    return {
      codigo: 'sin-sesion',
      mensaje: 'Tu sesión venció o no está iniciada. Volvé a entrar y probá de nuevo.',
    };
  }
  if (res && res.status === 403 && !codigo) {
    return {
      codigo: 'sin-permiso',
      mensaje: delServidor
        || 'Tu usuario no tiene permiso para reenviar el aviso (autoexcluidos:editar).',
    };
  }
  if (delServidor) return { codigo, mensaje: delServidor };
  if (error && error.message) return { codigo, mensaje: error.message };
  return { codigo, mensaje: porDefecto };
}

/* `marcaDeTiempo` es un ISO CON hora generado en el servidor, así que acá
   `Date` es correcto. (Las fechas SIN hora del aviso —`alta`, `vencimiento`—
   se formatean por string en el servidor: `new Date("2026-05-14")` en Jujuy
   devuelve el 13. Eso no se hace en este archivo.) */
function fechaHora(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Argentina/Buenos_Aires',
  });
}

/* Defensa barata contra mandar dos veces el mismo aviso a las mismas
   empresas: se lee del `cambios[]` que la ficha YA tiene en memoria — sin una
   consulta más. Se compara sin acentos porque el `tipo` que escribe el
   servidor lleva tilde ("Reenvío") y un día puede no llevarla. */
function ultimoReenvio(autoexcluido) {
  const cambios = (autoexcluido && Array.isArray(autoexcluido.cambios))
    ? autoexcluido.cambios
    : [];
  const esReenvio = (c) => String((c && c.tipo) || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .includes('reenvio de aviso de alta');

  let mejor = null;
  cambios.forEach((c) => {
    if (!esReenvio(c)) return;
    const t = new Date(c.marcaDeTiempo).getTime();
    if (Number.isNaN(t)) return;
    if (!mejor || t > mejor.t) mejor = { t, cambio: c };
  });
  return mejor ? mejor.cambio : null;
}

export default function ReenviarAvisoAlta({ autoexcluido, show, onHide, onEnviado }) {
  const auth = useAuth();
  const esSuperadmin = !!(auth && auth.esSuperadmin);

  const [estado, setEstado] = useState('cargando'); // cargando | listo | vacio | error
  const [errorLista, setErrorLista] = useState(null);
  const [origen, setOrigen] = useState(null);       // "config" | "roles-fallback"
  const [lista, setLista] = useState([]);
  const [tildados, setTildados] = useState([]);     // emails normalizados

  const [enviando, setEnviando] = useState(false);
  const [probando, setProbando] = useState(false);
  const [errorEnvio, setErrorEnvio] = useState(null);
  const [resultado, setResultado] = useState(null);

  /* Guarda anti-reentrada. El `disabled` del botón depende del render; un
     doble clic rápido dispara los dos handlers antes de que React repinte, y
     serían dos tandas de correos a las mismas empresas. El ref se lee y se
     escribe en el mismo tick. */
  const enVueloRef = useRef(false);

  const id = autoexcluido && autoexcluido.id;
  const previo = ultimoReenvio(autoexcluido);

  const cargar = useCallback(async () => {
    setEstado('cargando');
    setErrorLista(null);
    setErrorEnvio(null);
    setResultado(null);
    setOrigen(null);
    setLista([]);
    setTildados([]);
    try {
      const { data } = await axios.get(API + RUTA_DESTINATARIOS);
      const crudos = (data && Array.isArray(data.destinatarios)) ? data.destinatarios : [];

      /* Se normaliza y se deduplica también acá. El servidor ya lo hace, pero
         el email es la CLAVE de la casilla: dos entradas con el mismo mail en
         distinta caja tildarían y destildarían la misma fila. */
      const vistos = new Set();
      const limpios = [];
      crudos.forEach((d) => {
        const email = norm(d && d.email);
        if (!email || vistos.has(email)) return;
        vistos.add(email);
        limpios.push({
          email,
          /* El string EXACTO que mandó el servidor. Él intersecta la selección
             contra su propia lista y rechaza con `destinatarios-invalidos` lo
             que no coincida; `email` normalizado se usa sólo como clave de la
             casilla. Hoy los dos valores son iguales —el GET ya normaliza—,
             pero devolver lo recibido no depende de que eso siga siendo así. */
          crudo: String((d && d.email) || ''),
          nombre: (d && d.nombre) ? String(d.nombre) : email,
          origen: (d && d.origen) || null,
        });
      });

      setOrigen((data && data.origen) || null);
      setLista(limpios);
      setTildados(limpios.map((d) => d.email)); // todos tildados por defecto
      /* Un 200 con la lista vacía es el mismo hecho que el 404: no hay a
         quién avisarle. Una lista de casillas vacía sin explicación sería una
         pantalla muda (N1). */
      setEstado(limpios.length ? 'listo' : 'vacio');
    } catch (error) {
      const e = leerError(error, 'No se pudo consultar la lista de destinatarios.');
      if (e.codigo === 'sin-destinatarios-configurados') {
        setEstado('vacio');
        return;
      }
      console.error('[ReenviarAvisoAlta] No se pudo traer la lista:', error);
      setErrorLista({
        mensaje: QUE_HACER[e.codigo] ? e.mensaje + ' ' + QUE_HACER[e.codigo] : e.mensaje,
      });
      setEstado('error');
    }
  }, []);

  /* ⚠️ Trampa 1 del repo: un efecto que corre UNA vez al montar. Este depende
     de `show` y del ID, así que abrir el modal de OTRA ficha vuelve a pedir la
     lista y limpia el resultado del reenvío anterior. Sin `id` en las
     dependencias, el segundo autoexcluido mostraría el "enviado" del primero. */
  useEffect(() => {
    if (!show) return;
    cargar();
  }, [show, id, cargar]);

  /* Escape: window en fase de CAPTURA, y no `keyboard` de react-bootstrap.

     La ficha de `feature/home-comercial` se dibuja dentro de `Dialogo`, que
     escucha `keydown` en `document` en fase de captura y hace
     `stopPropagation()`. react-bootstrap escucha en `document` en fase de
     BURBUJA — o sea que nunca se entera del Escape, y el único que lo atiende
     es el `Dialogo`: la tecla cerraría LA FICHA ENTERA y dejaría este modal
     colgado. Es la misma trampa que ya está documentada en `FichaPersona`.

     `window` en captura corre ANTES que `document` en captura, así que acá el
     `stopPropagation` sí llega primero. Mientras el modal está abierto, Escape
     lo cierra a él y a nadie más. En `main`, donde no hay `Dialogo`, el efecto
     es el mismo y no molesta a nadie.

     No cierra con una escritura en vuelo: la respuesta dice a quiénes se les
     mandó, y esa es la única pantalla donde se lee. */
  useEffect(() => {
    if (!show) return undefined;
    const alTeclado = (e) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (enVueloRef.current) return;
      onHide();
    };
    window.addEventListener('keydown', alTeclado, true);
    return () => window.removeEventListener('keydown', alTeclado, true);
  }, [show, onHide]);

  const alternar = (email) => {
    setErrorEnvio(null);
    setTildados((prev) => (prev.includes(email)
      ? prev.filter((e) => e !== email)
      : prev.concat(email)));
  };

  const enviar = async (dryRun) => {
    if (enVueloRef.current) return;          // anti doble clic
    if (!id || !tildados.length) return;
    enVueloRef.current = true;
    const marcar = dryRun ? setProbando : setEnviando;
    marcar(true);
    setErrorEnvio(null);
    setResultado(null);
    try {
      const aMandar = lista
        .filter((d) => tildados.includes(d.email))
        .map((d) => d.crudo);

      const { data } = await axios.post(API + RUTA_REENVIO, {
        autoexcluidoId: id,
        destinatarios: aMandar,
        dryRun: !!dryRun,
      });

      /* Un 200 con la lista vacía adentro NO es un éxito. Es exactamente la
         forma del bug que originó esta feature: la pantalla decía "cargado
         correctamente" y no había salido nada. Si el servidor no nombra ni una
         dirección, esto se muestra como falla y no como aviso enviado (S2). */
      const enviados = (data && Array.isArray(data.correosEnviados)) ? data.correosEnviados : [];
      if (!dryRun && enviados.length === 0) {
        console.error('[ReenviarAvisoAlta] 200 sin correosEnviados:', data);
        setErrorEnvio({
          mensaje: 'El servidor respondió que salió bien pero no nombró ninguna dirección, '
            + 'así que no hay forma de saber si el aviso se encoló.',
          extra: ['Verificá en el Panel de Auditoría antes de volver a intentar, '
            + 'para no mandar el mismo aviso dos veces.'],
        });
        return;
      }

      setResultado({
        dryRun: !!(data && data.dryRun),
        /* Las direcciones LITERALES. No un contador: quien aprieta el botón
           tiene que poder leer a quién se le avisó — es lo que faltó durante
           cuatro meses. */
        correosEnviados: enviados,
        mailEnviadoA: (data && Array.isArray(data.mailEnviadoA)) ? data.mailEnviadoA : [],
        destinatarios: (data && Array.isArray(data.destinatarios)) ? data.destinatarios : [],
        advertencias: (data && Array.isArray(data.advertencias)) ? data.advertencias : [],
      });
      if (!dryRun && typeof onEnviado === 'function') onEnviado(data);
    } catch (error) {
      const e = leerError(error, 'No se pudo reenviar el aviso.');
      const cuerpo = (error && error.response && error.response.data) || {};
      const extra = [];
      if (Array.isArray(cuerpo.rechazados) && cuerpo.rechazados.length) {
        extra.push('Direcciones rechazadas: ' + cuerpo.rechazados.join(', '));
      }
      if (Array.isArray(cuerpo.faltan) && cuerpo.faltan.length) {
        extra.push('Faltan estos datos en la ficha: ' + cuerpo.faltan.join(', '));
      }
      if (QUE_HACER[e.codigo]) extra.push(QUE_HACER[e.codigo]);
      console.error('[ReenviarAvisoAlta] Falló el reenvío:', error);
      setErrorEnvio({ mensaje: e.mensaje, extra });
    } finally {
      marcar(false);
      enVueloRef.current = false;
    }
  };

  const trabajando = enviando || probando;
  const total = lista.length;
  const elegidos = tildados.length;
  const nombrePersona = (autoexcluido && autoexcluido.nombre) || '';
  const dniPersona = (autoexcluido && autoexcluido.dni) || '';

  const cerrar = () => { if (!enVueloRef.current) onHide(); };

  return (
    <Modal
      show={show}
      onHide={cerrar}
      centered
      backdrop={trabajando ? 'static' : true}
      /* El Escape lo maneja el efecto de arriba, por el conflicto con el
         `Dialogo` de la ficha. Dejar los dos activos sería cerrar dos veces. */
      keyboard={false}
      dialogClassName="rav-dialogo"
      aria-labelledby="rav-titulo"
    >
      <div className="rav">
        <div className="rav-cab">
          <div className="rav-cab-txt">
            <h2 className="rav-titulo" id="rav-titulo">Reenviar aviso de alta</h2>
            <p className="rav-sub">
              {nombrePersona
                ? <><span className="rav-sub-nombre">{nombrePersona}</span>
                  {dniPersona ? <> · DNI <span className="rav-num">{dniPersona}</span></> : null}</>
                : 'Autoexcluido sin nombre registrado'}
            </p>
          </div>
          <button
            type="button"
            className="rav-x"
            onClick={cerrar}
            disabled={trabajando}
            aria-label="Cerrar"
          >
            <span aria-hidden="true">×</span>
          </button>
        </div>

        <div className="rav-cuerpo">
          {/* ── RESULTADO ─────────────────────────────────────────────────── */}
          {resultado ? (
            <div className={'rav-final ' + (resultado.dryRun ? 'es-prueba' : 'es-ok')}>
              <p className="rav-final-tit">
                {resultado.dryRun
                  ? 'Prueba en seco: no se notificó a nadie de afuera'
                  : 'Aviso reenviado'}
              </p>

              {resultado.dryRun ? (
                <>
                  {resultado.mailEnviadoA.length > 0 && (
                    <>
                      <p className="rav-final-lead">El correo de prueba salió a:</p>
                      <ul className="rav-final-lista">
                        {resultado.mailEnviadoA.map((e) => <li key={e}>{e}</li>)}
                      </ul>
                    </>
                  )}
                  <p className="rav-final-lead">
                    En el envío real se le avisaría a {resultado.destinatarios.length}
                    {resultado.destinatarios.length === 1 ? ' destinatario:' : ' destinatarios:'}
                  </p>
                  <ul className="rav-final-lista">
                    {resultado.destinatarios.map((e) => <li key={e}>{e}</li>)}
                  </ul>
                  <p className="rav-final-pie">
                    No se escribió nada en el historial de la ficha ni en la auditoría.
                  </p>
                </>
              ) : (
                <>
                  <p className="rav-final-lead">
                    Se encoló el correo para {resultado.correosEnviados.length}
                    {resultado.correosEnviados.length === 1 ? ' destinatario:' : ' destinatarios:'}
                  </p>
                  <ul className="rav-final-lista">
                    {resultado.correosEnviados.map((e) => <li key={e}>{e}</li>)}
                  </ul>
                  <p className="rav-final-pie">
                    Quedó registrado en el historial de la ficha y en la auditoría.
                  </p>
                </>
              )}

              {resultado.advertencias.length > 0 && (
                <div className="rav-alerta es-ojo" role="status">
                  <p className="rav-alerta-tit">Salió con observaciones</p>
                  <ul className="rav-alerta-lista">
                    {resultado.advertencias.map((a, i) => <li key={i}>{a}</li>)}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* ── CONTEXTO ───────────────────────────────────────────────── */}
              {previo && (
                <p className="rav-previo" role="status">
                  <span className="rav-previo-tit">Ya se reenvió antes.</span>{' '}
                  Último reenvío: <b>{fechaHora(previo.marcaDeTiempo) || 'fecha no registrada'}</b>
                  {previo.usuario ? <> por <b>{previo.usuario}</b></> : null}.
                </p>
              )}

              {origen === 'roles-fallback' && estado === 'listo' && (
                <div className="rav-alerta es-ojo" role="alert">
                  <p className="rav-alerta-tit">Estos no son los correos de las empresas</p>
                  <p className="rav-alerta-txt">
                    No hay destinatarios cargados para el aviso de alta, así que la lista
                    salió de los usuarios del sistema con rol <b>admin</b>, <b>adminauto</b> o{' '}
                    <b>agente</b>. El aviso empieza con <i>“A La empresa:”</i> y le va a llegar a
                    personal de INPRO, no a las salas de juego. Cargá los destinatarios en{' '}
                    <b>Panel de Notificaciones</b> antes de reenviar.
                  </p>
                </div>
              )}

              {/* ── CARGANDO ───────────────────────────────────────────────── */}
              {estado === 'cargando' && (
                <div className="rav-carga" aria-busy="true" aria-live="polite">
                  <p className="rav-carga-txt">Buscando los destinatarios…</p>
                  {[0, 1, 2].map((i) => (
                    <div className="rav-skel-fila" key={i}>
                      <span className="rav-skel rav-skel-caja" />
                      <span className="rav-skel-col">
                        <span className="rav-skel rav-skel-l1" />
                        <span className="rav-skel rav-skel-l2" />
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {/* ── SIN DESTINATARIOS (N1: nunca una lista de ejemplo) ─────── */}
              {estado === 'vacio' && (
                <div className="rav-hueco">
                  <p className="rav-hueco-tit">No hay destinatarios configurados</p>
                  <p className="rav-hueco-txt">
                    El aviso de alta no tiene direcciones cargadas y tampoco hay usuarios con
                    los roles habilitados. Cargalos en <b>Panel de Notificaciones</b>, en el
                    tipo <b>Alta de autoexcluido</b>, y volvé a abrir esta ventana.
                  </p>
                </div>
              )}

              {/* ── FALLA AL TRAER LA LISTA (S2) ───────────────────────────── */}
              {estado === 'error' && (
                <div className="rav-hueco es-error" role="alert">
                  <p className="rav-hueco-tit">No se pudo traer la lista de destinatarios</p>
                  <p className="rav-hueco-txt">{errorLista && errorLista.mensaje}</p>
                  <button type="button" className="rav-btn rav-btn-fantasma" onClick={cargar}>
                    Reintentar
                  </button>
                </div>
              )}

              {/* ── LA LISTA ───────────────────────────────────────────────── */}
              {estado === 'listo' && (
                <fieldset className="rav-campo">
                  <legend className="rav-legend">
                    ¿A quiénes se les reenvía?
                  </legend>

                  <div className="rav-barra">
                    <p className="rav-cuenta" aria-live="polite">
                      <b>{elegidos}</b> de {total} {total === 1 ? 'destinatario' : 'destinatarios'}
                    </p>
                    <div className="rav-barra-acc">
                      <button
                        type="button"
                        className="rav-link"
                        onClick={() => setTildados(lista.map((d) => d.email))}
                        disabled={elegidos === total || trabajando}
                      >
                        Todos
                      </button>
                      <span className="rav-barra-sep" aria-hidden="true" />
                      <button
                        type="button"
                        className="rav-link"
                        onClick={() => setTildados([])}
                        disabled={elegidos === 0 || trabajando}
                      >
                        Ninguno
                      </button>
                    </div>
                  </div>

                  <ul className="rav-lista">
                    {lista.map((d) => {
                      const cajaId = 'rav-c-' + d.email.replace(/[^a-z0-9]/g, '-');
                      const marcado = tildados.includes(d.email);
                      return (
                        <li className={'rav-fila' + (marcado ? ' es-marcada' : '')} key={d.email}>
                          <input
                            type="checkbox"
                            id={cajaId}
                            className="rav-caja"
                            checked={marcado}
                            disabled={trabajando}
                            onChange={() => alternar(d.email)}
                          />
                          <label className="rav-fila-txt" htmlFor={cajaId}>
                            <span className="rav-fila-nombre">{d.nombre}</span>
                            <span className="rav-fila-mail">{d.email}</span>
                          </label>
                          {d.origen === 'rol' && (
                            <span className="rav-tag" title="Sale del listado de usuarios por rol">
                              por rol
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </fieldset>
              )}

              {/* ── FALLA AL ENVIAR (S2: el motivo, en pantalla) ───────────── */}
              {errorEnvio && (
                <div className="rav-alerta es-mal" role="alert">
                  <p className="rav-alerta-tit">No se reenvió el aviso</p>
                  <p className="rav-alerta-txt">{errorEnvio.mensaje}</p>
                  {errorEnvio.extra.map((t, i) => (
                    <p className="rav-alerta-txt" key={i}>{t}</p>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div className="rav-pie">
          {resultado ? (
            <>
              {resultado.dryRun && (
                <button
                  type="button"
                  className="rav-btn rav-btn-fantasma"
                  onClick={() => setResultado(null)}
                >
                  Volver a la lista
                </button>
              )}
              <button type="button" className="rav-btn rav-btn-primario" onClick={onHide}>
                Cerrar
              </button>
            </>
          ) : estado !== 'listo' ? (
            /* Sin lista no hay nada que confirmar. Un botón primario apagado
               que dice "Elegí al menos un destinatario" al lado de "no hay
               destinatarios configurados" le pide al operador algo que la
               pantalla no le está ofreciendo. */
            <button type="button" className="rav-btn rav-btn-primario" onClick={cerrar} disabled={trabajando}>
              Cerrar
            </button>
          ) : (
            <>
              <button
                type="button"
                className="rav-btn rav-btn-fantasma"
                onClick={cerrar}
                disabled={trabajando}
              >
                Cancelar
              </button>

              {/* Sólo superadmin: el servidor contesta 403
                  `dryrun-solo-superadmin` a cualquier otro. */}
              {esSuperadmin && (
                <button
                  type="button"
                  className="rav-btn rav-btn-fantasma"
                  onClick={() => enviar(true)}
                  disabled={trabajando || elegidos === 0}
                >
                  {probando ? 'Probando…' : 'Probar sin enviar'}
                </button>
              )}

              <button
                type="button"
                className="rav-btn rav-btn-primario"
                onClick={() => enviar(false)}
                disabled={trabajando || elegidos === 0}
              >
                {enviando
                  ? 'Reenviando…'
                  : elegidos === 0
                    ? 'Elegí al menos un destinatario'
                    : 'Reenviar a ' + elegidos + (elegidos === 1 ? ' destinatario' : ' destinatarios')}
              </button>
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}
